import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col } from 'react-bootstrap';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useStickyState } from '../libs/hooksLib';
import 'react-bootstrap-toggle/dist/bootstrap2-toggle.css';
import Toggle from 'react-bootstrap-toggle';

/**
 * Displays a modal that requests consent from Matomo.
 *
 * Text support markdown and is passed in as a prop.
 * Looks at matomo cookies:
 * mtm_consent, mtm_consent_removed and mtm_cookie_consent
 */
function MatomoConsent({
  setExternalOpen,
  externalOpen = false,
  ...props
}) {
  const [ showCookieWarning, setShowCookieWarning ]   = useStickyState(true, "showCookieWarning")
  const [ show, setShow ]                             = useState(showCookieWarning);
  const [ showSettings, setShowSettings ]             = useState(false);
  // If showCookieWarning was not set it means we haven't asked for consent yet.
  // Defaultt he slider to ON unless turned off.
  const [ consentGiven, setConsentGiven ]             = useState(showCookieWarning);
  const [ cookieConsentGiven, setCookieConsentGiven ] = useState(showCookieWarning);
  const { pushInstruction, trackLink }                = useMatomo();

  /**
   * Deletes all cookies and local storage on the current domain.
   */
  function clearLocal() {
    localStorage.clear();
    var cookies = document.cookie.split("; ");
    for (var c = 0; c < cookies.length; c++) {
        var d = window.location.hostname.split(".");
        while (d.length > 0) {
            var cookieBase = encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) + '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' + d.join('.') + ' ;path=';
            var p = window.location.pathname.split('/');
            document.cookie = cookieBase + '/';
            while (p.length > 0) {
                document.cookie = cookieBase + p.join('/');
                p.pop();
            };
            d.shift();
        }
    }
  };

  /**
   * Get the consent state on the first page-load.
   */
  useEffect(
    () => {
      pushInstruction(function () {
        setConsentGiven(showCookieWarning || this.hasConsent());
        setCookieConsentGiven(showCookieWarning || this.areCookiesEnabled());
      });
    },
    [pushInstruction, showCookieWarning]
  )

  /**
   * When opened by clicking 'manage consent' we want to go straight to granular
   * settings.
   */
   useEffect(
    () => {
      if(externalOpen) {
        setShowSettings(true);
      }
    },
    [externalOpen]
  )

  /**
   * Opens outlink in neww tab and tracks in Matomo.
   *
   * @param {*} event
   *    OnClick event. Will be preventDefault'ed.
   * @param {*} link
   *    Link to process outlink for.
   *
   * @returns 
   *    false.
   */
  function outlink(event, link) {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    event.preventDefault();
    trackLink({href: link, linkType: 'link'});
    window.open(link, '_blank').focus();
    return false;
  }

  function getNoCookieWarning() {
    if(cookieConsentGiven) {
      return(null);
    }
    return(
      <Row>
        <Col>
          <p className="text-danger">
            Without cookies we cannot remember your cookie preferences. You will
            have to configure your preferences every visit. If you allow cookies but
            withhold consent for tracking cookies will only be used where required
            for the functioning of the system.
          </p>
        </Col>
      </Row>
    );
  }

  /**
   * Gives the simple consent form.
   *
   * @returns react element.
   */
  function getMainPage() {
    return(
      <>
        <p>
          We use cookies to help the website function and track how you use our systems to
          allow us to improve our service to you. All data is stored securely on our
          systems and never shared with third parties. Read more&nbsp;
          <a
            href="https://www.cirencester-friendly.co.uk/short-form-privacy-notice"
            onClick={(event) => outlink(event, "https://www.cirencester-friendly.co.uk/short-form-privacy-notice")}
          >
            here.
          </a>
        </p>
        <p className="text-muted">
          You can change your preferences at any time by clicking "Manage consent" in
          the footer at the bottom of the page.<br/>
        </p>
        <Button
          className="me-2"
          onClick={
            () => {
              // Set consent.
              pushInstruction('rememberCookieConsentGiven');
              pushInstruction('rememberConsentGiven');
              // Remember not to show the banner.
              setShowCookieWarning(false);
              setShow(false);
              if(externalOpen) {
                setExternalOpen(false);
              }
            }
          }
        >Accept all</Button>
        <Button
          variant="light"
          onClick={
            () => {
              setShowSettings(true);
            }
          }
        >Manage consent</Button>
      </>
    )
  }

  /**
   * Gives the individual tracking settings.
   *
   * @returns react element.
   */
  function getSettingsPage() {
    return(
      <>
        <Row className="mb-2">
          <Col md={12} lg={11}>
            Allow the storage of cookies.
          </Col>
          <Col md={12} lg={1}>
            <Toggle
              onClick={() => {
                setCookieConsentGiven(!cookieConsentGiven);
              }}
              on="on"
              off="off"
              size="xs"
              offstyle="danger"
              active={cookieConsentGiven}
            />
          </Col>
        </Row>
        { getNoCookieWarning() }
        <Row className="mb-2">
          <Col md={12} lg={11}>
            Allow tracking of your use of this website to improve our service to
            you. Information is stored securely on our systems and never shared
            with third parties.
          </Col>
          <Col md={12} lg={1}>
            <Toggle
              onClick={() => {
                setConsentGiven(!consentGiven);
              }}
              on="on"
              off="off"
              size="xs"
              offstyle="danger"
              active={consentGiven}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <p className="text-muted">
              You can change your preferences at any time by clicking
              "Manage consent" in the footer at the bottom of the page.
            </p>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              className="me-2"
              onClick={
                () => {
                  // Save for Matomo.
                  setShow(false);
                  pushInstruction(consentGiven ? 'rememberConsentGiven' : 'forgetConsentGiven');
                  pushInstruction(cookieConsentGiven ? 'rememberCookieConsentGiven' : 'forgetCookieConsentGiven');
                  // Only store this locally if we're allowed...
                  if (cookieConsentGiven) {
                    setShowCookieWarning(false);
                  }
                  else {
                    // Clear cookies and local storage.
                    clearLocal();
                  }
                  if(externalOpen) {
                    setExternalOpen(false);
                  }
                  setShowSettings(false);
                }
              }
            >
              Save preferences    
            </Button>
          </Col>
        </Row>
      </>
    )
  }

  return (
    <Modal show={show || externalOpen} size="lg" onHide={ () => {} }>
      <Modal.Header>
        <Modal.Title>Consent</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {
          showSettings ? getSettingsPage() : getMainPage()
        }
      </Modal.Body>
    </Modal>
  );
}

MatomoConsent.propTypes = {
  /**
   * Function that can be used to change externalOpen.
   */
  setExternalOpen: PropTypes.func,
   /**
   * Indicates the modal has been opened form outside the component.
   */
  externalOpen: PropTypes.bool,
}

export default MatomoConsent;